import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, FileStack, PackageCheck, Eye, Trash2, Upload, Layers, Coins, IndianRupee, Boxes, Printer } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { NewMasterBillDialog } from "@/pages/admin/masterbills/NewMasterBillDialog";
import { ItemizeDialog } from "@/pages/admin/masterbills/ItemizeDialog";
import { OpeningPiecesImportDialog } from "@/pages/admin/masterbills/OpeningPiecesImportDialog";
import { api, formatINR, formatNum } from "@/lib/api";
import { toast } from "sonner";
import { hasAdminSession } from "@/lib/session";

export const BILL_STATUS_BADGE = {
  pending: "bg-[#FBF1DF] text-[#7A4B12] border border-[#E7D0A8]",
  partial: "bg-[#EAF1F7] text-[#1E3E5C] border border-[#C7D7E6]",
  itemized: "bg-[#E7F3EA] text-[#1F5B2E] border border-[#BFDCC7]",
  cancelled: "bg-[#F1E9E9] text-[#5C4444] border border-[#D8C6C6]",
};
export const SOURCE_BADGE = {
  supplier: "bg-[#FFF7E6] text-[#6A4B12] border border-[#E7D0A8]",
  karigar: "bg-[#EEF3F7] text-[#1E3E5C] border border-[#C7D7E6]",
  opening: "bg-[#F4EBDD] text-[#8E6F33] border border-[#E6D3AA]",
};
const PIECE_BADGE = {
  in_stock: "bg-[#E7F3EA] text-[#1F5B2E] border border-[#BFDCC7]",
  sold: "bg-[#F4EBDD] text-[#8E6F33] border border-[#E6D3AA]",
  reserved: "bg-[#EDE7FB] text-[#4A2E9A] border border-[#C8B8F0]",
  returned: "bg-[#FBE7E7] text-[#7A1F1F] border border-[#E7B9B9]",
  scrapped: "bg-[#F1E9E9] text-[#5C4444] border border-[#D8C6C6]",
  issued_to_karigar: "bg-[#E3EEFB] text-[#1F3F7A] border border-[#B9CCE7]",
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "-");

export default function MasterBills() {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [karigars, setKarigars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "open", source_type: "all", search: "" });
  const [newOpen, setNewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [itemizeBill, setItemizeBill] = useState(null);
  const [detail, setDetail] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.source_type !== "all") params.source_type = filters.source_type;
      if (filters.status !== "all" && filters.status !== "open") params.status = filters.status;
      if (filters.search) params.search = filters.search;
      const [{ data }, { data: sum }] = await Promise.all([
        api.get("/inventory/master-bills", { params }),
        api.get("/inventory/master-bills/summary"),
      ]);
      let list = data.bills || [];
      if (filters.status === "open") list = list.filter((b) => b.status === "pending" || b.status === "partial");
      setBills(list);
      setSummary(sum);
    } catch { toast.error("Could not load Master Bills"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!hasAdminSession()) { navigate("/admin/login"); return; }
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/purchase/suppliers", { params: { status: "active" } }).then(({ data }) => setSuppliers(data || [])).catch(() => {});
    api.get("/karigar/karigars").then(({ data }) => setKarigars(Array.isArray(data) ? data : (data?.karigars || []))).catch(() => {});
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status, filters.source_type, filters.search]);

  const refreshAll = async () => { await load(); api.get("/products").then(({ data }) => setProducts(data)).catch(() => {}); };

  const openDetail = async (b) => {
    try { const { data } = await api.get(`/inventory/master-bills/${b.id}`); setDetail(data); }
    catch { toast.error("Could not load bill"); }
  };
  const doDelete = async () => {
    try { await api.delete(`/inventory/master-bills/${deleteFor.id}`); toast.success(`${deleteFor.bill_no} deleted`); setDeleteFor(null); refreshAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const kpi = useMemo(() => summary || {}, [summary]);

  return (
    <AdminShell title="Master Bills">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Card className="p-4" data-testid="mb-kpi-open"><div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Open bills</span><FileStack className="h-4 w-4 text-muted-foreground" /></div><div className="font-mono text-2xl mt-2">{summary ? kpi.open_bills : <Skeleton className="h-7 w-12" />}</div><div className="text-xs text-muted-foreground mt-1">of {kpi.total_bills ?? 0} total</div></Card>
        <Card className="p-4" data-testid="mb-kpi-pending"><div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Pending pieces</span><Boxes className="h-4 w-4 text-[#7A4B12]" /></div><div className="font-mono text-2xl mt-2 text-[#7A4B12]">{summary ? kpi.pending_pieces : <Skeleton className="h-7 w-12" />}</div><div className="text-xs text-muted-foreground mt-1">waiting to be itemized</div></Card>
        <Card className="p-4" data-testid="mb-kpi-gold"><div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Unassigned gold</span><Coins className="h-4 w-4 text-muted-foreground" /></div><div className="font-mono text-2xl mt-2">{summary ? `${formatNum(kpi.pending_gold_g)}g` : <Skeleton className="h-7 w-16" />}</div><div className="text-xs text-muted-foreground mt-1">{formatINR(kpi.pending_inr)} unallocated</div></Card>
        <Card className="p-4" data-testid="mb-kpi-instock"><div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Itemized in vault</span><PackageCheck className="h-4 w-4 text-[#1F5B2E]" /></div><div className="font-mono text-2xl mt-2 text-[#1F5B2E]">{summary ? kpi.itemized_in_stock : <Skeleton className="h-7 w-12" />}</div><div className="text-xs text-muted-foreground mt-1">pieces in stock</div></Card>
      </div>

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input data-testid="mb-search" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="Bill no, source, reference" className="pl-9" />
          </div>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="mb-status-filter" className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open (to itemize)</SelectItem>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="partial">Partially itemized</SelectItem>
              <SelectItem value="itemized">Fully itemized</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.source_type} onValueChange={(v) => setFilters({ ...filters, source_type: v })}>
            <SelectTrigger data-testid="mb-source-filter" className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="supplier">Supplier</SelectItem>
              <SelectItem value="karigar">Karigar</SelectItem>
              <SelectItem value="opening">Opening balance</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="mb-import-opening-button" variant="outline" className="gap-2" onClick={() => setImportOpen(true)}><Upload className="h-4 w-4" /> Import Opening Pieces</Button>
          <Button data-testid="mb-new-bill-button" className="gap-2" onClick={() => setNewOpen(true)}><Plus className="h-4 w-4" /> New Master Bill</Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="master-bills-table">
            <TableHeader><TableRow className="bg-[#F4EBDD]">
              <TableHead>Bill</TableHead><TableHead>Source</TableHead><TableHead>Reference</TableHead>
              <TableHead>Itemized</TableHead><TableHead>Gold (g)</TableHead><TableHead>Diamonds (ct)</TableHead>
              <TableHead className="text-right">Cost</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading && [...Array(4)].map((_, i) => <TableRow key={i}><TableCell colSpan={9}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)}
              {!loading && bills.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-12">
                  <Layers className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <div className="text-sm font-medium">No master bills {filters.status === "open" ? "waiting to be itemized" : "found"}</div>
                  <div className="text-xs text-muted-foreground mt-1">Record a Supplier / Karigar bill, or import your opening pieces to get started.</div>
                  <Button data-testid="mb-empty-new-button" size="sm" className="mt-4 gap-1" onClick={() => setNewOpen(true)}><Plus className="h-3.5 w-3.5" /> New Master Bill</Button>
                </TableCell></TableRow>
              )}
              {!loading && bills.map((b) => {
                const pct = b.total_qty ? Math.round((b.assigned_qty / b.total_qty) * 100) : 0;
                const canItemize = b.status !== "cancelled" && b.unassigned_qty > 0;
                return (
                  <TableRow key={b.id} data-testid={`mb-row-${b.bill_no}`} className="hover:bg-[#F7F0E6]">
                    <TableCell><div className="font-mono text-xs font-medium">{b.bill_no}</div><div className="text-[11px] text-muted-foreground">{fmtDate(b.bill_date)}</div></TableCell>
                    <TableCell className="min-w-0"><div className="text-sm truncate max-w-[180px]">{b.source_name || "-"}</div><Badge className={`${SOURCE_BADGE[b.source_type] || ""} text-[10px] mt-0.5`}>{b.source_type}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{b.reference_no || "-"}</TableCell>
                    <TableCell className="min-w-[120px]"><div className="font-mono text-xs">{b.assigned_qty} / {b.total_qty} pcs</div><Progress value={pct} className="h-1.5 mt-1" /></TableCell>
                    <TableCell className="font-mono text-xs">{formatNum(b.assigned_gold_weight_g)} / {formatNum(b.total_gold_weight_g)}<div className="text-[10px] text-muted-foreground">{formatNum(b.unassigned_gold_weight_g)}g left</div></TableCell>
                    <TableCell className="font-mono text-xs">{Number(b.assigned_diamond_ct || 0).toFixed(2)} / {Number(b.total_diamond_ct || 0).toFixed(2)}</TableCell>
                    <TableCell className="font-mono text-xs text-right">{formatINR(b.total_inr_cost)}<div className="text-[10px] text-muted-foreground">{formatINR(b.unassigned_inr_cost)} left</div></TableCell>
                    <TableCell><Badge className={BILL_STATUS_BADGE[b.status] || ""}>{b.status}</Badge></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button data-testid={`mb-itemize-${b.bill_no}`} size="sm" variant={canItemize ? "default" : "outline"} className="gap-1 h-8" disabled={!canItemize} onClick={() => setItemizeBill(b)}><PackageCheck className="h-3.5 w-3.5" /> Itemize</Button>
                      <Button data-testid={`mb-view-${b.bill_no}`} size="icon" variant="ghost" title="View pieces" onClick={() => openDetail(b)}><Eye className="h-4 w-4" /></Button>
                      <Button data-testid={`mb-delete-${b.bill_no}`} size="icon" variant="ghost" title="Delete bill" disabled={b.assigned_qty > 0 || !!b.linked_purchase_id || !!b.linked_job_id} onClick={() => setDeleteFor(b)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      <NewMasterBillDialog open={newOpen} onOpenChange={setNewOpen} suppliers={suppliers} karigars={karigars} onCreated={refreshAll} />
      <ItemizeDialog open={!!itemizeBill} onOpenChange={(o) => !o && setItemizeBill(null)} bill={itemizeBill} products={products} onDone={refreshAll} />
      <OpeningPiecesImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={refreshAll} />

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="master-bill-detail-dialog" className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><FileStack className="h-5 w-5 text-[hsl(var(--primary))]" /> {detail?.bill?.bill_no}</DialogTitle>
            <DialogDescription>{detail?.bill?.source_name} · {detail?.bill?.source_type}{detail?.bill?.reference_no ? ` · Ref ${detail.bill.reference_no}` : ""} · {fmtDate(detail?.bill?.bill_date)}</DialogDescription>
          </DialogHeader>
          {detail && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  ["Pieces", `${detail.bill.assigned_qty} / ${detail.bill.total_qty}`],
                  ["Gold", `${formatNum(detail.bill.assigned_gold_weight_g)} / ${formatNum(detail.bill.total_gold_weight_g)} g`],
                  ["Diamonds", `${Number(detail.bill.assigned_diamond_ct || 0).toFixed(2)} / ${Number(detail.bill.total_diamond_ct || 0).toFixed(2)} ct`],
                  ["Cost", `${formatINR(detail.bill.assigned_inr_cost)} / ${formatINR(detail.bill.total_inr_cost)}`],
                ].map(([l, v]) => <div key={l} className="rounded-lg border border-border bg-[#F7F0E6] p-3"><div className="text-xs uppercase text-muted-foreground">{l}</div><div className="font-mono text-sm mt-1">{v}</div></div>)}
              </div>
              {detail.bill.notes && <p className="text-xs text-muted-foreground">{detail.bill.notes}</p>}
              <div className="overflow-x-auto rounded-lg border border-border">
                <Table data-testid="master-bill-pieces-table">
                  <TableHeader><TableRow className="bg-[#F4EBDD]"><TableHead>Barcode</TableHead><TableHead>Product</TableHead><TableHead>Gross</TableHead><TableHead>Net</TableHead><TableHead>Dia</TableHead><TableHead className="text-right">Cost</TableHead><TableHead>Status</TableHead><TableHead>Sold on</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {detail.pieces.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground text-sm">Nothing itemized from this bill yet.</TableCell></TableRow>}
                    {detail.pieces.map((pc) => (
                      <TableRow key={pc.id}>
                        <TableCell className="font-mono text-xs">{pc.barcode}{(pc.huid || pc.stone_certificate_no) && <div className="text-[10px] text-muted-foreground whitespace-nowrap">{pc.huid ? `HUID ${pc.huid}` : ""}{pc.huid && pc.stone_certificate_no ? " · " : ""}{pc.stone_certificate_no ? `Cert ${pc.stone_certificate_no}` : ""}</div>}</TableCell>
                        <TableCell className="min-w-0"><div className="text-sm truncate max-w-[180px]">{pc.product_name}</div><div className="text-[11px] font-mono text-muted-foreground">{pc.product_sku}</div></TableCell>
                        <TableCell className="font-mono text-xs">{pc.gross_weight_g}g</TableCell>
                        <TableCell className="font-mono text-xs">{pc.net_weight_g}g</TableCell>
                        <TableCell className="font-mono text-xs">{pc.diamond_carat_total ? `${pc.diamond_carat_total}ct` : "-"}</TableCell>
                        <TableCell className="font-mono text-xs text-right">{formatINR(pc.unit_cost)}</TableCell>
                        <TableCell><Badge className={PIECE_BADGE[pc.status] || ""}>{(pc.status || "").replace(/_/g, " ")}</Badge></TableCell>
                        <TableCell className="font-mono text-xs">{pc.sold_ref_no || "-"}</TableCell>
                        <TableCell className="text-right"><Button size="icon" variant="ghost" title="Print tag" onClick={() => window.open(`/admin/print-tags?barcodes=${encodeURIComponent(pc.barcode)}`, "_blank", "noopener")}><Printer className="h-4 w-4 text-[hsl(var(--primary))]" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
          <DialogFooter>
            {detail?.bill?.unassigned_qty > 0 && detail.bill.status !== "cancelled" && <Button data-testid="detail-itemize-button" className="gap-1" onClick={() => { setItemizeBill(detail.bill); setDetail(null); }}><PackageCheck className="h-4 w-4" /> Itemize remaining {detail.bill.unassigned_qty}</Button>}
            <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete {deleteFor?.bill_no}?</AlertDialogTitle><AlertDialogDescription>Only bills with nothing itemized can be deleted. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-bill-button" onClick={doDelete} className="bg-destructive text-white">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}
